/**
 * Theme: dark, light, or whatever the operating system says.
 *
 * Three ideas hold this together.
 *
 * **The default is the system preference, not a colour.** A developer who has told
 * their OS they want light mode has already answered this question, and asking again
 * with a dark page is how a product announces it did not check.
 *
 * **`data-theme` on <html> only ever holds a RESOLVED value** — `light` or `dark`,
 * never `system`. The preference and the resolution are different things: the
 * preference lives in localStorage and drives this module, the resolution lives in
 * the DOM and drives the CSS. That split is why global.css needs a single
 * `:root[data-theme='light']` block instead of the same palette written twice, once
 * behind a `prefers-color-scheme` media query.
 *
 * **First paint is settled before React runs.** The inline script in index.html
 * applies the same rule this module does, so there is no dark flash on a light
 * machine. Both read STORAGE_KEY; if you change it here, change it there — the
 * comment in index.html says so too.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

/** Shared with the pre-paint script in index.html. */
const STORAGE_KEY = 'chi-theme'

export type ThemePreference = 'system' | 'light' | 'dark'
export type ResolvedTheme = 'light' | 'dark'

interface ThemeContextValue {
  /** What the reader chose. `system` means "keep following the OS". */
  preference: ThemePreference
  /** What that currently resolves to, and therefore what is on <html>. */
  resolved: ResolvedTheme
  setPreference: (next: ThemePreference) => void
  /**
   * Advance to the next option in the cycle: system → light → dark → system.
   *
   * Cycling through three states rather than toggling two keeps "follow my OS"
   * reachable. A binary switch silently strands the reader on an explicit choice
   * they can never give back.
   */
  cycle: () => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

const DARK_QUERY = '(prefers-color-scheme: dark)'

/** The substrate colour per theme, for the browser-chrome `theme-color` meta. */
const THEME_COLOR: Record<ResolvedTheme, string> = { dark: '#0a0d0c', light: '#f6f8f7' }

function readPreference(): ThemePreference {
  if (typeof window === 'undefined') return 'system'
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored
  } catch {
    // Safari in private mode throws on localStorage access rather than returning
    // null. A theme preference is not worth a blank page.
  }
  return 'system'
}

function systemTheme(): ResolvedTheme {
  return typeof matchMedia === 'function' && matchMedia(DARK_QUERY).matches ? 'dark' : 'light'
}

function resolve(preference: ThemePreference): ResolvedTheme {
  return preference === 'system' ? systemTheme() : preference
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setStoredPreference] = useState<ThemePreference>(readPreference)
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolve(readPreference()))

  // Apply, then persist. Writing the attribute in an effect rather than during
  // render keeps this a side effect of the state it mirrors.
  useEffect(() => {
    const next = resolve(preference)
    setResolved(next)
    document.documentElement.dataset.theme = next
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR[next])
    try {
      localStorage.setItem(STORAGE_KEY, preference)
    } catch {
      // As above — an unwritable store costs the choice on the next visit, nothing more.
    }
  }, [preference])

  // Follow the OS while, and only while, the reader has not overridden it. Someone
  // whose machine flips at sunset should see the page flip with it.
  useEffect(() => {
    if (preference !== 'system' || typeof matchMedia !== 'function') return
    const query = matchMedia(DARK_QUERY)
    const onChange = () => {
      const next = systemTheme()
      setResolved(next)
      document.documentElement.dataset.theme = next
      document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR[next])
    }
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [preference])

  const cycle = useCallback(() => {
    setStoredPreference((current) =>
      current === 'system' ? 'light' : current === 'light' ? 'dark' : 'system',
    )
  }, [])

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, resolved, setPreference: setStoredPreference, cycle }),
    [preference, resolved, cycle],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext)
  if (!value) throw new Error('useTheme must be used inside ThemeProvider')
  return value
}
